/**
 * TaskCommentThread: Real comment thread for task detail panel.
 *
 * SAKME-BOARD-AUTH-1: Replaces the "comments coming soon" placeholder.
 * Fetches messages from GET /tasks/{id}/comments, sends via POST.
 * Polls every 10s for new messages (Phase 1, no WebSocket).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageSquare, Loader2, Bot } from 'lucide-react';
import { taskComments } from '@/api/engine';
import type { Message } from '@/api/types';

// ── Author avatar helper ────────────────────────────────

function AuthorAvatar({ name, type }: { name: string; type: string }) {
  const initials = (name || '?')
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  const bgColor = type === 'agent'
    ? 'bg-[#39FF14]/15 text-[#39FF14]'
    : type === 'alter_ego'
      ? 'bg-amber-500/15 text-amber-400'
      : 'bg-blue-500/15 text-blue-400';

  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${bgColor}`}>
      {type === 'agent' ? <Bot className="w-3.5 h-3.5" /> : initials}
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Component ───────────────────────────────────────────

interface TaskCommentThreadProps {
  taskId: string;
  readOnly?: boolean;
}

export function TaskCommentThread({ taskId, readOnly = false }: TaskCommentThreadProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const msgs = await taskComments.list(taskId);
      setMessages(msgs);
      setError(null);
    } catch {
      // Backend may not have the endpoint yet — graceful degradation
      if (loading) setError(null); // Don't show error on initial load — just empty state
    } finally {
      setLoading(false);
    }
  }, [taskId, loading]);

  // Initial fetch
  useEffect(() => {
    setMessages([]);
    setLoading(true);
    setError(null);
    void fetchMessages();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 10s
  useEffect(() => {
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const msg = await taskComments.post(taskId, text);
      setMessages((prev) => [...prev, msg]);
      setInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  }, [taskId, input, sending]);

  return (
    <div
      className="rounded-lg border p-3.5"
      style={{
        borderColor: 'var(--theme-surface-tertiary, #222836)',
        backgroundColor: 'rgba(0,0,0,0.15)',
      }}
    >
      <label
        className="text-[10px] font-mono uppercase flex items-center gap-1.5 mb-3"
        style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
      >
        <MessageSquare className="w-3 h-3" />
        Comments
        {messages.length > 0 && (
          <span className="ml-1 text-gray-500">{messages.length}</span>
        )}
      </label>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
        </div>
      )}

      {/* Empty state */}
      {!loading && messages.length === 0 && !error && (
        <div className="text-xs font-mono text-gray-600 italic py-4 text-center">
          No comments yet. Start the conversation.
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-xs text-red-400 py-2">{error}</div>
      )}

      {/* Message list */}
      {messages.length > 0 && (
        <div ref={scrollRef} className="space-y-3 max-h-64 overflow-y-auto mb-3">
          {messages.map((msg) => (
            <div key={msg.id} className="flex items-start gap-2.5">
              <AuthorAvatar
                name={msg.sender_display_name ?? 'Unknown'}
                type={msg.author_type}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium" style={{ color: 'var(--theme-text-primary, #f4f7fb)' }}>
                    {msg.sender_display_name ?? 'Unknown'}
                  </span>
                  {msg.author_type === 'agent' && (
                    <span className="text-[9px] font-mono px-1 py-px rounded bg-[#39FF14]/10 text-[#39FF14]">agent</span>
                  )}
                  <span className="text-[10px] text-gray-600 ml-auto shrink-0">
                    {formatRelativeTime(msg.sent_at)}
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}>
                  {msg.content_text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      {!readOnly && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
            placeholder="Add a comment..."
            disabled={sending}
            className="flex-1 rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--theme-surface-tertiary, #222836)',
              color: 'var(--theme-text-primary, #f4f7fb)',
            }}
          />
          <button
            onClick={() => void handleSend()}
            disabled={!input.trim() || sending}
            className="p-1.5 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-30"
          >
            {sending
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--theme-accent-primary, var(--sp-accent))' }} />
              : <Send className="w-3.5 h-3.5" style={{ color: 'var(--theme-accent-primary, var(--sp-accent))' }} />
            }
          </button>
        </div>
      )}
      {readOnly && (
        <div className="text-[10px] text-gray-600 italic text-center pt-2">
          Read-only mode — comments disabled
        </div>
      )}
    </div>
  );
}
